import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components/native";
import { DeviceEventEmitter, Pressable, View } from "react-native";
import { Input, Spacer, VDBText } from "@components/atoms";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { SelectionType } from "@components/atoms/Filters/types";
import { SpaceType } from "@components/atoms/Spacer/types";
import RangeFilter from "@components/atoms/Filters/RangeFilter";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useDispatch } from "react-redux";
import {
  removeFiltersPayload,
  setFiltersPayload,
  useSearchFiltersSlice,
  useValueFromPayloadRange,
} from "@redux/slices/searchFilters";
import { SelectedItemTheme } from "@components/atoms/Button/types";
import { useTranslation } from "react-i18next";
import ShowMoreButton from "../ShowMoreButton";
import useResetFilter from "../../../../hooks/useResetFilter";
import { VDBIcons } from "@utils/VDBIcons";
import { handleBlurMinMaxRef } from "../FilterSection/Utils";
import { useTheme } from "styled-components/native";
import { FilterScreenContext } from "@scenes/explore/FilterScreenContext";
import { ProductCategory } from "@utils/constants";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { css } from "styled-components/native";
import { getMinMaxForShape, normalizeBoolean } from "../ButtonSection/Utils";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { formatNumberWithPrecision } from "@utils/product/productFormatters";

type RangeListSectionProps = {
  options: Array<any>;
  columns: number;
  selectionType?: string;
  sectionTitle?: string;
  payloadName?: Array<string>;
  sectionName?: string;
  selectedStyle: SelectedItemTheme;
  minLabel?: string;
  maxLabel?: string;
  displayedRows?: number;
  direction?: string;
  minWidth?: number;
  web_width?: number;
  step?: number;
  selectedProduct: ProductCategory;
  enterSecondFlow?: boolean;
  isCollapsible?: boolean;
};

const RangeListSection = ({
  options: initialOptions = [],
  selectionType = SelectionType.range,
  sectionTitle = "",
  payloadName = [],
  sectionName = "",
  selectedStyle,
  minLabel = "",
  maxLabel = "",
  displayedRows = 1,
  direction = "column",
  minWidth = 72,
  web_width = 0,
  step = 1,
  selectedProduct = ProductCategory.DIAMOND,
  enterSecondFlow = false,
  isCollapsible = false,
}: RangeListSectionProps) => {
  if (!(initialOptions?.length > 0)) {
    return null;
  }
  const userInfo = useUserInfo();
  const [minValueError, setMinValueError] = useState<string | null>(null);
  const [maxValueError, setMaxValueError] = useState<string | null>(null);
  const basePayloadKey = Array.isArray(payloadName)
    ? payloadName?.[0]
    : payloadName;
  const baseSectionKey =
    basePayloadKey || sectionTitle || sectionName || "options";
  const safeKey = (value: unknown) =>
    String(value ?? "item")
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9._-]/g, "");
  const baseTestId = `filters.${
    sectionName?.toLowerCase()?.replaceAll(" ", "-")?.toString() || "section"
  }.${safeKey(baseSectionKey)}.rangeList`;
  const mEnterSecondFlow = normalizeBoolean(enterSecondFlow);
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const { jcSelectedOption } = useJewelryConfiguratorState();
  const { auxPayload: filtersPayload, min_max_Options } =
    useSearchFiltersSlice();
  const modifyShapeSupportForBuilder =
    min_max_Options?.supported_shapes?.length > 0 &&
    userInfo.user.feature_settings?.allow_search_modify_in_builder &&
    enableJewelryBuilder &&
    mEnterSecondFlow;
  const shapeFilter = Array.isArray(filtersPayload[sectionName]?.shapes)
    ? filtersPayload[sectionName]?.shapes[0]
    : filtersPayload[sectionName]?.shapes;

  const { minOption, maxOption } = getMinMaxForShape(
    modifyShapeSupportForBuilder as boolean,
    min_max_Options,
    shapeFilter
  ) || {
    min: null,
    max: null,
  };

  let supportedFrom = modifyShapeSupportForBuilder
    ? minOption
    : jcSelectedOption?.current?.option_value?.supported_carat_from;
  let supportedTo = modifyShapeSupportForBuilder
    ? maxOption
    : jcSelectedOption?.current?.option_value?.supported_carat_to;

  supportedFrom = formatNumberWithPrecision(Number(supportedFrom), {
    precision: 2,
  });
  supportedTo = formatNumberWithPrecision(Number(supportedTo), {
    precision: 2,
  });

  let mInitialOptions = initialOptions;

  if (enableJewelryBuilder && mEnterSecondFlow === true) {
    mInitialOptions = initialOptions.filter((option) => {
      const [min, max] = option.search_payload || [];
      return min >= supportedFrom && max <= supportedTo;
    });
  }

  const value = useValueFromPayloadRange(sectionName, payloadName);
  const { containerWidth } = useContext(FilterScreenContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const useWeb_width =
    web_width && step === 1 && (theme.isLargeScreen || theme.isTablet);
  const effectiveWebWidth = useWeb_width ? web_width : 0;
  const adjustedContainerWidth =
    containerWidth * ((100 - effectiveWebWidth) / 100);
  const itemsPerRow = Math.floor(
    adjustedContainerWidth /
      (minWidth + (useWeb_width ? SpaceType.large : SpaceType.Extra))
  );
  const columnsWidth = useMemo(() => {
    const columnWidth = Math.floor(
      (adjustedContainerWidth -
        (itemsPerRow - 1) *
          (useWeb_width ? SpaceType.Double : SpaceType.Extra)) /
        itemsPerRow
    );
    return columnWidth;
  }, [mInitialOptions, itemsPerRow, containerWidth]);

  const options = useMemo(() => {
    if (itemsPerRow === 0) return mInitialOptions;
    const numberOfItems = mInitialOptions.length;
    const numberOfPlaceholders = itemsPerRow - (numberOfItems % itemsPerRow);
    return [...mInitialOptions, ...Array(numberOfPlaceholders).fill({})];
  }, [mInitialOptions, itemsPerRow]);

  const rows = Math.min(
    displayedRows ?? 0,
    Math.ceil(options.length / itemsPerRow)
  );

  const minAndMaxRef = useRef({
    min:
      value?.[0] !== undefined && !isNaN(parseFloat(value[0]))
        ? formatNumberWithPrecision(value[0], { precision: 2 })
        : null,
    max:
      value?.[1] !== undefined && !isNaN(parseFloat(value[1]))
        ? formatNumberWithPrecision(value[1], { precision: 2 })
        : null,
  });

  const [inputMin, setInputMin] = useState(minAndMaxRef.current.min);
  const [inputMax, setInputMax] = useState(minAndMaxRef.current.max);

  useEffect(() => {
    const listener = DeviceEventEmitter.addListener(
      "caratRangeValid",
      (data: { from: number; to: number }) => {
        const carateErrorMessage = t("searchFilter.carat.error.text", {
          x: supportedFrom,
          y: supportedTo,
        });

        setMinValueError(
          data.from < Number(supportedFrom) || data.from > Number(supportedTo)
            ? carateErrorMessage
            : ""
        );
        setMaxValueError(
          data.to < Number(supportedFrom) || data.to > Number(supportedTo)
            ? carateErrorMessage
            : ""
        );
      }
    );

    return () => {
      listener.remove();
    };
  }, [supportedFrom, supportedTo]);

  const handleMinBlur = () => {
    if (
      Number(inputMin) >= Number(supportedFrom) &&
      Number(inputMin) <= Number(supportedTo)
    ) {
      setMinValueError(null);
    }
  };

  const handleMaxBlur = () => {
    if (
      Number(inputMax) >= Number(supportedFrom) &&
      Number(inputMax) <= Number(supportedTo)
    ) {
      setMaxValueError(null);
    }
  };

  const dispatch = useDispatch();
  const isTouched = useRef(false);
  const isInputChange = useRef(false);
  const lastPayloadRef = useRef<{ min: string | null; max: string | null }>({
    min: minAndMaxRef.current.min,
    max: minAndMaxRef.current.max,
  });
  const typography = useTypographyStore();
  const [startRangeIndex, setStartRangeIndex] = useState<number | null>(null);
  const [showAll, setShowAll] = useState<boolean>(!displayedRows);

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const lastRangeKeyRef = useRef<string>("");
  const selectedItems = useMemo(() => {
    if (!inputMin || !inputMax) return [];

    const minIndex = options.findIndex(
      (option) =>
        option?.search_payload?.[0] !== undefined &&
        Number(option.search_payload[0]).toFixed(2) ===
          Number(inputMin).toFixed(2)
    );
    const maxIndex = options.findIndex(
      (option) =>
        option?.search_payload?.[1] !== undefined &&
        Number(option.search_payload[1]).toFixed(2) ===
          Number(inputMax).toFixed(2)
    );

    if (minIndex === -1 || maxIndex === -1) return [];
    return options.slice(minIndex, maxIndex + 1);
  }, [inputMin, inputMax, options]);

  useResetFilter(() => {
    setInputMin(null);
    setInputMax(null);
    setStartRangeIndex(null);
  });

  useEffect(() => {
    const rangeKey = `${selectedProduct ?? ""}-${sectionName ?? ""}-${
      payloadName?.[0] ?? ""
    }`;

    if (lastRangeKeyRef.current && lastRangeKeyRef.current !== rangeKey) {
      setStartRangeIndex(null);
    }
    lastRangeKeyRef.current = rangeKey;
  }, [payloadName, sectionName, selectedProduct]);

  const formatValue = useCallback((caratValue: any): string => {
    if (typeof caratValue === "number" && Number.isFinite(caratValue)) {
      const formatted = formatNumberWithPrecision(caratValue, { precision: 2 });
      return formatted ?? "0.00"; // fallback if formatter returns null
    }
    return "0.00";
  }, []);

  const handleMinMaxChange = (text: string, isMin: boolean) => {
    isInputChange.current = true;
    isTouched.current = true;

    const cleaned = text.replace(/,/g, ".").replace(/[^0-9.]/g, "");

    if (isMin) {
      setInputMin(cleaned);
      minAndMaxRef.current.min = cleaned === "" ? null : cleaned;
    } else {
      setInputMax(cleaned);
      minAndMaxRef.current.max = cleaned === "" ? null : cleaned;
    }
  };

  useEffect(() => {
    if (!isTouched.current) return;

    const payload: any = {};
    const nextMin = inputMin !== null && inputMin !== "" ? inputMin : null;
    const nextMax = inputMax !== null && inputMax !== "" ? inputMax : null;
    if (
      lastPayloadRef.current.min === nextMin &&
      lastPayloadRef.current.max === nextMax
    ) {
      return;
    }

    if (inputMin === null && inputMax === null && sectionName && payloadName) {
      dispatch(removeFiltersPayload({ sectionName, payloadName }));
    } else {
      if (inputMin !== null && inputMin !== "") {
        payload[payloadName[0]] = parseFloat(inputMin).toString();
      } else payload[payloadName[0]] = "";
      if (inputMax !== null && inputMax !== "") {
        payload[payloadName[1]] = parseFloat(inputMax).toString();
      } else payload[payloadName[1]] = "";
      if (sectionName) {
        dispatch(setFiltersPayload({ sectionName, data: payload }));
      }
    }
    lastPayloadRef.current = { min: nextMin, max: nextMax };
  }, [inputMin, inputMax]);

  useEffect(() => {
    const [min, max] = value || [];

    const formattedMin =
      min !== undefined && !isNaN(parseFloat(min))
        ? formatNumberWithPrecision(min, { precision: 2 })
        : "";
    const formattedMax =
      max !== undefined && !isNaN(parseFloat(max))
        ? formatNumberWithPrecision(max, { precision: 2 })
        : "";

    minAndMaxRef.current = {
      min: formattedMin || null,
      max: formattedMax || null,
    };
    lastPayloadRef.current = {
      min: formattedMin || null,
      max: formattedMax || null,
    };

    setInputMin(formattedMin || null);
    setInputMax(formattedMax || null);
  }, []);

  const handleItemSelection = useCallback(
    (item: any, index: number) => {
      isInputChange.current = false;
      isTouched.current = true;

      if (selectionType === SelectionType.range) {
        const clickedMin = formatValue(item?.search_payload?.[0] || 0);
        const clickedMax = formatValue(item?.search_payload?.[1] || 0);
        const isCurrentlySelected =
          inputMin === clickedMin && inputMax === clickedMax;
        const currentMinIndex = options.findIndex(
          (option) =>
            option?.search_payload?.[0] !== undefined &&
            formatValue(option.search_payload[0]) === inputMin
        );
        const currentMaxIndex = options.findIndex(
          (option) =>
            option?.search_payload?.[1] !== undefined &&
            formatValue(option.search_payload[1]) === inputMax
        );
        if (startRangeIndex == null) {
          if (
            currentMinIndex !== -1 &&
            currentMinIndex === currentMaxIndex &&
            index !== currentMinIndex
          ) {
            const minIndex = Math.min(currentMinIndex, index);
            const maxIndex = Math.max(currentMinIndex, index);
            setInputMin(
              formatValue(options[minIndex]?.search_payload?.[0] || 0)
            );
            setInputMax(
              formatValue(options[maxIndex]?.search_payload?.[1] || 0)
            );
            return;
          }
          if (
            currentMinIndex !== -1 &&
            currentMaxIndex !== -1 &&
            currentMinIndex !== currentMaxIndex
          ) {
            if (index === currentMaxIndex + 1) {
              setInputMax(
                formatValue(
                  options[currentMaxIndex + 1]?.search_payload?.[1] || 0
                )
              );
              return;
            }
            if (index === currentMinIndex - 1) {
              setInputMin(
                formatValue(
                  options[currentMinIndex - 1]?.search_payload?.[0] || 0
                )
              );
              return;
            }
            if (index === currentMinIndex) {
              setInputMin(
                formatValue(
                  options[currentMinIndex + 1]?.search_payload?.[0] || 0
                )
              );
              return;
            }
            if (index === currentMaxIndex) {
              setInputMax(
                formatValue(
                  options[currentMaxIndex - 1]?.search_payload?.[1] || 0
                )
              );
              return;
            }
          }
          if (isCurrentlySelected) {
            setInputMin(null);
            setInputMax(null);
            setStartRangeIndex(null);
            return;
          }
          setStartRangeIndex(index);
          setInputMin(clickedMin);
          setInputMax(clickedMax);
          return;
        }

        const start = startRangeIndex;
        if (
          currentMinIndex !== -1 &&
          currentMinIndex === currentMaxIndex &&
          index !== currentMinIndex
        ) {
          const minIndex = Math.min(currentMinIndex, index);
          const maxIndex = Math.max(currentMinIndex, index);
          setInputMin(formatValue(options[minIndex]?.search_payload?.[0] || 0));
          setInputMax(formatValue(options[maxIndex]?.search_payload?.[1] || 0));
          setStartRangeIndex(null);
          return;
        }
        if (start === index && isCurrentlySelected) {
          setInputMin(null);
          setInputMax(null);
          setStartRangeIndex(null);
          return;
        }
        const minIndex = Math.min(start, index);
        const maxIndex = Math.max(start, index);

        setInputMin(formatValue(options[minIndex]?.search_payload?.[0] || 0));
        setInputMax(formatValue(options[maxIndex]?.search_payload?.[1] || 0));
        setStartRangeIndex(null);
        return;
      }

      const clickedMin = formatValue(item?.search_payload?.[0] || 0);
      const clickedMax = formatValue(item?.search_payload?.[1] || 0);
      if (inputMin === clickedMin && inputMax === clickedMax) {
        setInputMin(null);
        setInputMax(null);
        return;
      }
      setInputMin(clickedMin);
      setInputMax(clickedMax);
    },
    [
      formatValue,
      inputMax,
      inputMin,
      options,
      payloadName,
      selectionType,
      startRangeIndex,
    ]
  );

  useEffect(() => {
    if (selectedItems?.length > 0) {
      setShowAll(
        selectedItems.some(
          (item) => options.indexOf(item) + 1 > rows * itemsPerRow
        )
      );
    }
  }, []);

  useEffect(() => {
    if (!isTouched.current || isInputChange.current) return;

    if (!inputMin && !inputMax) {
      if (enableJewelryBuilder && mEnterSecondFlow === true) {
        let mSupportedFrom = modifyShapeSupportForBuilder
          ? minOption
          : jcSelectedOption?.current?.option_value?.supported_carat_from;
        let mSupportedTo = modifyShapeSupportForBuilder
          ? maxOption
          : jcSelectedOption?.current?.option_value?.supported_carat_to;
        mSupportedFrom = Number(mSupportedFrom).toFixed(2);
        mSupportedTo = Number(mSupportedTo).toFixed(2);
        setInputMin(mSupportedFrom);
        setInputMax(mSupportedTo);
        minAndMaxRef.current.min = mSupportedFrom;
        minAndMaxRef.current.max = mSupportedTo;
      } else {
        minAndMaxRef.current.min = null;
        minAndMaxRef.current.max = null;
      }
    }
  }, [
    enableJewelryBuilder,
    inputMax,
    inputMin,
    mEnterSecondFlow,
    minOption,
    maxOption,
    modifyShapeSupportForBuilder,
    jcSelectedOption,
  ]);

  return (
    <ParentContainer>
      {sectionTitle && !isCollapsible && (
        <>
          <TitleSection>
            <VDBText isBold type={typography["H3-M"]} color={theme["core-05"]}>
              {sectionTitle}
            </VDBText>
            <Spacer size={SpaceType.Extra} />
          </TitleSection>
        </>
      )}
      <View
        style={{
          flexDirection: direction === "column" ? "column" : "row",
          alignItems:
            direction === "row" && (theme.isLargeScreen || theme.isTablet)
              ? "flex-start"
              : undefined,
        }}
      >
        <CompareView
          useWeb_width={useWeb_width}
          effectiveWebWidth={effectiveWebWidth}
        >
          <View style={{ flex: 1 }}>
            <Input
              inputLabel={t(minLabel || "", { defaultValue: "Min" })}
              value={inputMin ? inputMin.toString() : ""}
              onChangeText={(text) => handleMinMaxChange(text, true)}
              onBlur={() => {
                handleBlurMinMaxRef(true, minAndMaxRef, setInputMin, false);
                handleMinBlur();
              }}
              inputType="decimal-pad"
              selectedStyle={selectedStyle}
              {...(enableJewelryBuilder && mEnterSecondFlow === true
                ? {
                    error: minValueError ?? "",
                    renderSpaceForErrorMessage: true,
                  }
                : {})}
              testID={`${baseTestId}.minInput`}
              data-testId={`${baseTestId}.minInput`}
            />
          </View>

          <VDBIcon
            name={VDBIcons.ARROW_RIGHT}
            size={24}
            color={theme["core-04"]}
            variant={IconsVariant.VDBCustom}
          />

          <View style={{ flex: 1 }}>
            <Input
              inputLabel={t(maxLabel || "", { defaultValue: "Max" })}
              value={inputMax ? inputMax : ""}
              onChangeText={(text) => handleMinMaxChange(text, false)}
              onBlur={() => {
                handleBlurMinMaxRef(false, minAndMaxRef, setInputMax, false);
                handleMaxBlur();
              }}
              inputType="decimal-pad"
              selectedStyle={selectedStyle}
              {...(enableJewelryBuilder && mEnterSecondFlow === true
                ? {
                    error: maxValueError ?? "",
                    renderSpaceForErrorMessage: true,
                  }
                : {})}
              testID={`${baseTestId}.maxInput`}
              data-testId={`${baseTestId}.maxInput`}
            />
          </View>
        </CompareView>
        {enableJewelryBuilder &&
          mEnterSecondFlow === true &&
          (minValueError || maxValueError) && (
            <>
              <Spacer size={SpaceType.Double} />
              <VDBText type={typography["Minor-R"]} color={theme["error-01"]}>
                {minValueError || maxValueError}
              </VDBText>
            </>
          )}
        <Spacer size={SpaceType.Double} />
        <RangeView direction={direction} showAll={showAll}>
          {options
            ?.slice(0, showAll ? options?.length : rows * itemsPerRow)
            .map((item, index) => {
              const optionKey = safeKey(
                item?.payload_name?.[0] ||
                  item?.search_payload?.[0] ||
                  item?.name ||
                  item?.label ||
                  index
              );
              const optionTestId = `${baseTestId}.option.${optionKey}`;
              return (
                <Pressable
                  key={item.name}
                  onHoverIn={() => setHoveredIndex(index)}
                  onHoverOut={() => setHoveredIndex(null)}
                  onPress={() => handleItemSelection(item, index)}
                  disabled={!item?.label}
                  testID={optionTestId}
                  data-testId={optionTestId}
                >
                  <RangeTab
                    children={""}
                    isSelected={selectedItems.includes(item)}
                    width={columnsWidth}
                    selectedStyle={selectedStyle}
                    label={item?.label}
                    isHovered={index === hoveredIndex}
                  />
                </Pressable>
              );
            })}
        </RangeView>
      </View>
      <ShowMoreButton
        options={options}
        columns={itemsPerRow}
        showAll={showAll}
        onPressFunction={setShowAll}
        displayedRows={rows}
        selectedProduct={selectedProduct}
      />
    </ParentContainer>
  );
};

const CompareView = styled.View<{
  useWeb_width?: boolean;
  effectiveWebWidth?: number;
}>`
  gap: ${SpaceType.Basic}px;
  flex-direction: row;
  align-items: center;
  ${({
    useWeb_width,
    effectiveWebWidth,
  }: {
    useWeb_width?: boolean;
    effectiveWebWidth?: number;
  }) =>
    useWeb_width
      ? css`
          width: ${effectiveWebWidth}%;
        `
      : css`
          width: 100%;
        `};
`;

const RangeView = styled.View<{ direction?: string; showAll?: boolean }>`
  gap: ${SpaceType.Extra}px;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  ${({ direction }: { direction?: string }) =>
    direction === "column" ? "flex-grow: 1;" : "flex: 1;"}
  ${({ direction }: { direction?: string }) =>
    direction === "column"
      ? `padding-top:${SpaceType.Basic}px;`
      : "padding-top: 0px;"}
`;

const RangeTab = styled(RangeFilter)<{
  width: number;
  isSelected: boolean;
}>`
  width: ${({ width }: { width: number }) => width}px;
`;

const ParentContainer = styled.View`
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
`;

const TitleSection = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-top: ${SpaceType.Extra}px;
  padding-bottom: ${SpaceType.Extra}px;
`;

export default memo(RangeListSection);
